import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { type Editor } from '@tiptap/react';
import useT from '@tools/ui-hooks/useT';
import { theme } from '../../theme/semanticTokens';
import { EmojiPickerPopover } from './EmojiPickerPopover';

interface ToolbarProps {
  editor: Editor;
  showEmojiInToolbar?: boolean;
}

const ToolbarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: ${theme.dime.spacing.s};
  border-bottom: 1px solid ${theme.dime.colors.border.static};
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${theme.dime.radius.s};
  background: ${({ $active }) =>
    $active ? theme.dime.colors.surface.primarySelected : 'transparent'};
  color: ${theme.dime.colors.text.primary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 0;

  &:hover {
    background: ${theme.dime.colors.surface.primaryHover};
  }
`;

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background: ${theme.dime.colors.border.static};
  margin: 0 4px;
`;

export const Toolbar: React.FC<ToolbarProps> = ({
  editor,
  showEmojiInToolbar = true
}) => {
  const { t } = useT();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker((prev) => !prev);
  }, []);

  const insertEmoji = useCallback(
    (emoji: string) => {
      editor.chain().focus().insertContent(emoji).run();
      setShowEmojiPicker(false);
    },
    [editor]
  );

  return (
    <ToolbarWrapper>
      <ToolbarButton
        type="button"
        $active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title={t('richTextEditor.Bold')}
      >
        {t('richTextEditor.B')}
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title={t('richTextEditor.Italic')}
        style={{ fontStyle: 'italic' }}
      >
        {t('richTextEditor.I')}
      </ToolbarButton>
      <ToolbarButton
        type="button"
        $active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title={t('richTextEditor.Strikethrough')}
        style={{ textDecoration: 'line-through' }}
      >
        {t('richTextEditor.S')}
      </ToolbarButton>

      {!!showEmojiInToolbar && (
        <>
          <Separator />
          <ToolbarButton
            ref={emojiButtonRef}
            type="button"
            $active={showEmojiPicker}
            onClick={toggleEmojiPicker}
            title={t('richTextEditor.Emoji')}
            style={{ fontSize: '16px', fontWeight: 'normal' }}
          >
            {'\u{2705}'}
          </ToolbarButton>
          {!!showEmojiPicker && (
            <EmojiPickerPopover
              anchorRef={emojiButtonRef}
              onSelect={insertEmoji}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}
        </>
      )}
    </ToolbarWrapper>
  );
};
